/**
 * detailV2 — 상세 페이지 v2(Stitch) 전용 순수 어댑터 (2026-09-21, 배치 5).
 *
 * 포함:
 *   - 가격 성격(원분양가/할인가) 판정 + 캡션        getPriceBasis · priceBasisLabel · buildPriceCaption
 *   - 스펙바 4칸                                    buildSpecCells
 *   - 타입별 분양가 표 행                            buildTypePriceRows
 *   - 청약일정 6단계                                buildScheduleSteps
 *   - 인근 실거래 비교(요약값)                       buildDealCompareRows
 *   - 섹션 v2 순서 정렬(관리자 커스텀 섹션 위치 보존) orderDetailSections
 *   - 할인 분양가 커스텀 섹션 있으면 타입별 분양가 제외  isDiscountPriceSection · dropDuplicatePriceSection
 *
 * 정책:
 *   - fetch 없음. 데이터 없으면 빈 배열/null (호출부가 섹션 미렌더).
 *   - 가격 성격은 DB 값으로만 판정한다. 프론트에 현장 ID 하드코딩 금지.
 *     기준 = 관리자가 가격 섹션(auto 'price') 제목에 '할인'을 명시한 현장 → 가격 필드가 할인가.
 *     (214: 대표 지시 2026-08-21 로 min/max_price·type_prices 가 할인 적용가로 전환되며
 *      섹션 제목이 '분양가 (할인 적용가)' 로 바뀌었다. 그 외 현장은 청약홈 공고가 = 원분양가.)
 *   - announcement_url·applyhome_url 등 외부 링크는 다루지 않는다.
 */

import { propertyPyeongRange, typePyeongLabel } from './pyeong';
import type { Property } from '../../services/api';
import { formatUnits } from './formatters';

// ─────────────────────────────────────────────
// 가격 성격
// ─────────────────────────────────────────────
export type PriceBasis = 'official' | 'discounted';

interface SectionLike {
  kind?: string;
  key?: string;
  title?: string | null;
}

export function getPriceBasis(property: Property): PriceBasis {
  const sections = (property as any)?.sections as SectionLike[] | undefined;
  if (!Array.isArray(sections)) return 'official';
  const discounted = sections.some(
    (s) => s && s.kind === 'auto' && s.key === 'price' && /할인/.test(s.title ?? ''),
  );
  return discounted ? 'discounted' : 'official';
}

export function priceBasisLabel(basis: PriceBasis): string {
  return basis === 'discounted' ? '할인가' : '원분양가';
}

/** 대표 혜택이 분양가 인하(할인율/할인액)인지. */
function priceCutHeadline(property: Property): string | null {
  const h = property.benefit_headline;
  if (!h || !h.text) return null;
  return h.kind === 'price_cut' ? h.text : null;
}

/**
 * 가격 캡션 (대표 결정 5: 공고가 표시 + 원분양가/할인 적용 표기).
 *   - 할인가(가격 필드가 할인 적용가): "할인 적용가(타입별 최고가) · 원분양가 대비 최대 22% 할인"
 *   - 원분양가 + 분양가 인하 혜택: "원분양가 · 현재 최대 22% 할인 적용 중"
 *   - 원분양가 + 인하 혜택 없음: "원분양가 · 입주자모집공고 기준"
 * phoneDisplay 가 있으면 정확한 금액은 전화 상담 문구를 덧붙인다(할인 현장만).
 */
export function buildPriceCaption(property: Property, phoneDisplay?: string): string {
  const basis = getPriceBasis(property);
  const cut = priceCutHeadline(property);
  const tail = phoneDisplay ? ` · 정확한 금액은 ${phoneDisplay} 상담` : '';
  if (basis === 'discounted') {
    return `할인 적용가(타입별 최고가 기준)${cut ? ` · 원분양가 대비 ${cut}` : ''}${tail}`;
  }
  if (cut) return `원분양가 · 현재 ${cut} 적용 중${tail}`;
  return '원분양가 · 입주자모집공고 기준';
}

// ─────────────────────────────────────────────
// 금액 포맷 (만원)
// ─────────────────────────────────────────────
/** 54800 → "5억 4,800만", 10000 → "1억", 3500 → "3,500만" */
export function formatManwonShort(v: number): string {
  if (v >= 10000) {
    const eok = Math.floor(v / 10000);
    const rem = v % 10000;
    return rem === 0 ? `${eok}억` : `${eok}억 ${rem.toLocaleString()}만`;
  }
  return `${v.toLocaleString()}만`;
}

/** 55800 → "5.58억", 135550 → "13.55억"(소수 둘째 자리 내림 — 과장 금지), 8000 → "8,000만" */
export function formatEokCompact(v: number): string {
  if (v >= 10000) {
    const s = (Math.floor(v / 100) / 100).toFixed(2).replace(/\.?0+$/, '');
    return `${s}억`;
  }
  return `${v.toLocaleString()}만`;
}

const isPos = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v) && v > 0;

// ─────────────────────────────────────────────
// 스펙바
// ─────────────────────────────────────────────
export interface SpecCell {
  key: string;
  label: string;
  value: string;
  sub?: string;
  accent?: boolean;
}

function parseTopFloor(scale?: string | null): number | null {
  if (!scale) return null;
  const m = scale.match(/지상\s*(\d{1,3})\s*층/) || scale.match(/최고\s*(\d{1,3})\s*층/);
  return m ? parseInt(m[1], 10) : null;
}

function formatArea(v: number): string {
  return Number.isInteger(v) ? String(v) : String(Math.round(v));
}

function formatYm(raw?: string | null): string | null {
  if (!raw) return null;
  const m = String(raw).match(/^(\d{4})-(\d{1,2})/);
  if (m) return `${m[1]}.${m[2].padStart(2, '0')}`;
  const k = String(raw).match(/(\d{4})\s*년\s*(\d{1,2})\s*월/);
  if (k) return `${k[1]}.${k[2].padStart(2, '0')}`;
  return null;
}

export function buildSpecCells(property: Property): SpecCell[] {
  const cells: SpecCell[] = [];

  const floors = parseTopFloor(property.building_scale);
  const units = isPos(property.total_units) ? property.total_units : null;
  if (floors || units) {
    cells.push({
      key: 'scale',
      label: '규모',
      value: floors ? `${floors}층` : formatUnits(property, { short: true }),
      sub: floors && units ? formatUnits(property, { short: true }) : undefined,
    });
  }

  const minA = isPos(property.min_area) ? property.min_area : null;
  const maxA = isPos(property.max_area) ? property.max_area : null;
  if (minA || maxA) {
    const value =
      minA && maxA && Math.round(minA) !== Math.round(maxA)
        ? `${formatArea(minA)}~${formatArea(maxA)}㎡`
        : `${formatArea((minA || maxA) as number)}㎡`;
    // 2026-09-26 대표: 평으로 보여야 이해하기 쉽다 — 공급 기준 평수를 값으로, 전용㎡는 보조
    //   공급면적이 없으면(평수가 '전용 N평') 기존 '전용 ㎡' 칸을 유지하고 평수를 보조로
    const py = propertyPyeongRange(property);
    cells.push(py && !py.startsWith('전용')
      ? { key: 'area', label: '평형', value: py, sub: `전용 ${value}` }
      : { key: 'area', label: '전용', value, sub: py || property.property_type || undefined });
  }

  const minP = isPos(property.min_price) ? property.min_price : isPos(property.max_price) ? property.max_price : null;
  if (minP) {
    cells.push({
      key: 'price',
      label: priceBasisLabel(getPriceBasis(property)),
      value: `${formatEokCompact(minP)}~`,
      sub: isPos(property.price_per_pyeong) ? `평당 ${property.price_per_pyeong.toLocaleString()}만` : undefined,
      accent: true,
    });
  }

  const ym = formatYm(property.move_in_date);
  if (ym) cells.push({ key: 'movein', label: '입주', value: ym });

  return cells;
}

// ─────────────────────────────────────────────
// 타입별 분양가 표
// ─────────────────────────────────────────────
export interface TypePriceRow {
  type: string;
  /** 공급 기준 평수 "35평" (공급 없으면 "전용 25평") — 2026-09-26 */
  pyeong: string | null;
  exclusive: string | null;
  supply: string | null;
  price: string;
  /** 공고문 최저가(확정값, 최고가보다 작을 때만) — 있으면 '최저 ~ 최고' 두 줄 (2026-10-01) */
  priceMin: string | null;
  units: number | null;
  recentDeal: string | null;
  dealComparePct: number | null;
}

const fmtM2 = (v: number | null | undefined) => (isPos(v) ? `${v.toFixed(2)}㎡` : null);

export function buildTypePriceRows(property: Property): TypePriceRow[] {
  const tps = property.type_prices;
  if (!Array.isArray(tps) || tps.length === 0) return [];
  return tps
    .filter((tp) => isPos(tp.top_price))
    .slice()
    .sort((a, b) => a.type_name.localeCompare(b.type_name, 'ko', { numeric: true }))
    .map((tp) => ({
      type: tp.type_name,
      pyeong: typePyeongLabel(tp),
      exclusive: fmtM2(tp.exclusive_area),
      supply: fmtM2(tp.supply_area),
      price: formatManwonShort(tp.top_price as number),
      priceMin:
        isPos(tp.min_price) && (tp.min_price as number) < (tp.top_price as number)
          ? formatManwonShort(tp.min_price as number)
          : null,
      units: isPos(tp.supply_count) ? tp.supply_count : null,
      recentDeal: isPos(tp.recent_deal_price) ? formatManwonShort(tp.recent_deal_price) : null,
      dealComparePct: typeof tp.deal_compare_pct === 'number' ? tp.deal_compare_pct : null,
    }));
}

// ─────────────────────────────────────────────
// 인근 실거래 비교 (대표 결정 2 — type_prices 요약값, deal_count>=3 일 때만 서버가 pct 계산)
// ─────────────────────────────────────────────
export interface DealCompareRow {
  type: string;
  recentDeal: string;
  price: string;
  pct: number;
  dealCount: number;
  dealDate: string | null;
}

export function buildDealCompareRows(property: Property): DealCompareRow[] {
  const tps = property.type_prices;
  if (!Array.isArray(tps)) return [];
  return tps
    .filter((tp) => typeof tp.deal_compare_pct === 'number' && isPos(tp.recent_deal_price) && isPos(tp.top_price))
    .map((tp) => ({
      type: tp.type_name,
      recentDeal: formatManwonShort(tp.recent_deal_price as number),
      price: formatManwonShort(tp.top_price as number),
      pct: tp.deal_compare_pct as number,
      dealCount: tp.deal_count,
      dealDate: tp.recent_deal_date ? String(tp.recent_deal_date).slice(0, 7).replace('-', '.') : null,
    }));
}

// ─────────────────────────────────────────────
// 청약일정 6단계
// ─────────────────────────────────────────────
export type StepState = 'done' | 'now' | 'upcoming';

export interface ScheduleStep {
  key: string;
  label: string;
  date: string;
  state: StepState;
}

const ISO = /^\d{4}-\d{2}-\d{2}/;
const d10 = (v?: string | null): string | null => (v && ISO.test(v) ? v.slice(0, 10) : null);
const dot = (iso: string) => iso.replace(/-/g, '.');

function rangeLabel(a: string, b: string | null): string {
  if (!b || a === b) return dot(a);
  // 같은 해면 뒤쪽은 MM.DD
  return a.slice(0, 4) === b.slice(0, 4) ? `${dot(a)} ~ ${dot(b).slice(5)}` : `${dot(a)} ~ ${dot(b)}`;
}

function stateOf(start: string, end: string | null, today: string): StepState {
  const e = end ?? start;
  if (e < today) return 'done';
  if (start <= today && today <= e) return 'now';
  return 'upcoming';
}

/**
 * 모집공고 · 특별공급 · 1·2순위 · 당첨자 발표 · 정당계약 · 입주.
 * 날짜 없는 단계는 만들지 않는다. 6단계가 전부 비면 subscription_start~end 한 줄 폴백,
 * 그것도 없으면 빈 배열.
 */
export function buildScheduleSteps(property: Property, today: string): ScheduleStep[] {
  const steps: ScheduleStep[] = [];
  const push = (key: string, label: string, a: string | null, b: string | null = null) => {
    if (!a) return;
    steps.push({ key, label, date: rangeLabel(a, b), state: stateOf(a, b, today) });
  };

  push('announcement', '입주자 모집공고', d10(property.announcement_date));
  push('special', '특별공급', d10(property.special_supply_date));
  const r1 = d10(property.general_rank1_date);
  const r2 = d10(property.general_rank2_date);
  if (r1 || r2) push('rank', r1 && r2 ? '1·2순위 청약' : r1 ? '1순위 청약' : '2순위 청약', (r1 || r2) as string, r1 && r2 ? r2 : null);
  push('winner', '당첨자 발표', d10(property.winner_announcement_date));
  const cs = d10(property.contract_start);
  const ce = d10(property.contract_end);
  if (cs || ce) push('contract', '정당계약', (cs || ce) as string, cs && ce ? ce : null);

  const moveYm = (property.move_in_date ?? '').match(/^(\d{4})-(\d{1,2})/);
  if (moveYm) {
    const ym = `${moveYm[1]}-${moveYm[2].padStart(2, '0')}`;
    const tYm = today.slice(0, 7);
    steps.push({
      key: 'movein',
      label: '입주 예정',
      date: `${moveYm[1]}년 ${Number(moveYm[2])}월`,
      state: ym < tYm ? 'done' : ym === tYm ? 'now' : 'upcoming',
    });
  }

  const hasSix = steps.some((s) => s.key !== 'movein');
  if (!hasSix) {
    const ss = d10(property.subscription_start);
    const se = d10(property.subscription_end);
    if (ss) {
      steps.unshift({ key: 'subscription', label: '청약 접수', date: rangeLabel(ss, se), state: stateOf(ss, se, today) });
    }
  }
  return steps;
}

// ─────────────────────────────────────────────
// 섹션 순서 (v2): 혜택 → 분양가 → 평면도 → 청약일정 → (나머지)
// ─────────────────────────────────────────────
export interface OrderableSection {
  kind: string;
  key: string;
}

const V2_RANK: Record<string, number> = { benefit: 1, price: 2, floorplan: 3, schedule: 4 };

/**
 * DB 순서(관리자 CMS)를 존중하되 auto 핵심 4종을 v2 순서로 앞에 세운다.
 *   - custom 섹션은 DB 상 바로 앞 auto 섹션에 "붙어서" 함께 이동한다
 *     (예: 214 '할인 분양가 안내'는 분양가 뒤, '계약 일정 안내'는 청약일정 뒤 유지)
 *   - 반환: primary = v2 핵심 그룹(순서 정렬), rest = 그 외 그룹(DB 순서, basic 등)
 */
export function orderDetailSections<T extends OrderableSection>(
  sections: T[],
): { primary: T[]; rest: T[] } {
  const groups: { lead: T | null; items: T[] }[] = [];
  let current: { lead: T | null; items: T[] } = { lead: null, items: [] };
  for (const s of sections) {
    if (s.kind === 'auto') {
      if (current.items.length) groups.push(current);
      current = { lead: s, items: [s] };
    } else {
      current.items.push(s);
    }
  }
  if (current.items.length) groups.push(current);

  const rankOf = (g: { lead: T | null }) => (g.lead ? V2_RANK[g.lead.key] ?? 0 : 0);
  const primaryGroups = groups.filter((g) => rankOf(g) > 0).sort((a, b) => rankOf(a) - rankOf(b));
  const restGroups = groups.filter((g) => rankOf(g) === 0);
  return {
    primary: primaryGroups.flatMap((g) => g.items),
    rest: restGroups.flatMap((g) => g.items),
  };
}

// ─────────────────────────────────────────────
// 분양가 섹션 중복 제거 (대표 지시 2026-09-22)
//   "타입별 분양가와 아래 할인 분양가 안내 2번 중복 — 할인 분양가 안내만 있으면 되겠다" (214)
//   규칙: 내용이 있는 custom 섹션 중 제목에 '할인' + ('분양가' | '가격')이 들어간 것이 있으면
//         auto 'price'(타입별 분양가) 섹션을 렌더 목록에서 뺀다.
//   ⚠️ 원본 property.sections 는 건드리지 않는다 — getPriceBasis(할인가/원분양가 판정)가
//      auto 'price' 섹션 제목을 읽으므로, 렌더 목록에서만 제외한다.
// ─────────────────────────────────────────────
interface DiscountPriceSectionLike {
  kind?: string;
  key?: string;
  title?: string | null;
  body?: string | null;
  image_url?: string | null;
}

export function isDiscountPriceSection(s: DiscountPriceSectionLike | null | undefined): boolean {
  if (!s || s.kind !== 'custom') return false;
  const title = s.title ?? '';
  if (!/할인/.test(title) || !/분양가|가격/.test(title)) return false;
  const hasBody = !!(s.body && s.body.trim());
  const hasImage = !!(s.image_url && s.image_url.trim());
  return hasBody || hasImage; // 빈 커스텀 섹션은 렌더되지 않으므로 대체 근거가 못 된다
}

/**
 * @param sections 걸러낼 목록 (orderDetailSections 결과의 primary/rest 처럼 일부여도 된다)
 * @param all      할인 분양가 섹션 존재 여부를 판단할 전체 목록 (기본 = sections)
 *   → 정렬(orderDetailSections) 뒤에 적용해야 커스텀 섹션이 분양가 자리에 그대로 남는다.
 */
export function dropDuplicatePriceSection<T extends DiscountPriceSectionLike>(
  sections: T[],
  all: DiscountPriceSectionLike[] = sections,
): T[] {
  // 모델하우스(견본주택) 주소 섹션은 항상 제외 — 대표 지시 2026-09-22, 앞으로도 유지
  const visible = sections.some(isModelHouseSection) ? sections.filter((s) => !isModelHouseSection(s)) : sections;
  if (!all.some(isDiscountPriceSection)) return visible;
  return visible.filter((s) => !(s.kind === 'auto' && s.key === 'price'));
}

/** 관리자 커스텀 섹션 중 견본주택·모델하우스·홍보관 안내(주소) 섹션 */
export function isModelHouseSection(s: DiscountPriceSectionLike | null | undefined): boolean {
  if (!s || s.kind !== 'custom') return false;
  return /견본주택|모델하우스|홍보관/.test(s.title ?? '');
}
